/**
 * The Mum Hub Remote Config CONTRACT, shared between the app and the website
 * (MAR-138, MAR-235).
 *
 * Two parameters live in the Firebase console of the `capsule-chic-hub`
 * project, and both platforms read exactly these two:
 *
 * - `mum_hub_enabled`, a Boolean: the global switch. Kill switch now, public
 *   rollout later.
 * - `mum_hub_allowlist`, a String holding a JSON array of audience tokens:
 *   who gets in while that switch is off.
 *
 * NO CONDITIONS, NO CUSTOM SIGNALS, and nothing per-customer is ever sent to
 * Firebase. Every client fetches the same two values and decides on-device
 * whether ITS OWN locally computed token (`mum-hub-audience.ts`) is in the
 * array. The console never learns who asked.
 *
 * WHY THIS FILE EXISTS RATHER THAN THE WEB REIMPLEMENTING IT. Adrian's tester
 * was on the allow-list and saw the tab on her phone and a 404 on the
 * website, because the website's gate had never heard of Remote Config. The
 * fix is the same decision on both platforms, not a second one that happens
 * to agree today. `packages/cch-shared/src/__tests__/mum-hub-audience.test.ts`
 * pins every constant and both functions here against the app's own
 * `mobile/src/lib/feature-flags/mum-hub.ts`, so the two cannot drift.
 *
 * VISIBILITY ONLY. This decides whether the Mum Hub surface renders. It is
 * NOT an entitlement check and must never be treated as one — a client-side
 * boolean is trivially flipped by anyone with the binary or the bundle. Every
 * page it reveals still reads its content from the Worker behind a session
 * and a server-side entitlement check.
 *
 * FAIL CLOSED, everywhere. Unset key, unknown key, malformed allow-list,
 * fetch failure, timeout, offline, signed out, a thrown SDK, no Firebase
 * config at all — every one of them is `false`. The compiled-in defaults
 * below are `false` and an empty array, so a build that never reaches
 * Firebase shows nothing.
 */

/**
 * Remote Config parameter names. Must match the console exactly, and must
 * match the app, which is the point of them being here.
 */
export const MUM_HUB_FLAG_KEY = "mum_hub_enabled";
export const MUM_HUB_ALLOWLIST_KEY = "mum_hub_allowlist";

/**
 * Compiled-in defaults. Firebase serves these whenever it has no activated
 * remote value, which includes the very first page load and every offline
 * one before a successful fetch. Off and empty on purpose; both must stay
 * that way.
 */
export const MUM_HUB_FLAG_DEFAULT = false;
export const MUM_HUB_ALLOWLIST_DEFAULT = "[]";

/**
 * Remote Config fetch cache TTL.
 *
 * The SDK default is 12 hours, far too slow for something we may need to
 * switch off in a hurry. 30 minutes is the deliberate choice, and the same
 * number the app uses: short enough that a console flip reaches a client
 * within half an hour of cache age, long enough to stay well inside Remote
 * Config's client-side fetch throttling (which starts pushing back around
 * five fetches an hour).
 *
 * Use {@link MUM_HUB_FETCH_TTL_DEV_MS} in development instead, so console
 * changes can be verified without waiting.
 */
export const MUM_HUB_FETCH_TTL_MS = 30 * 60 * 1000;

/**
 * `0` in development only. On a dev server every reload is then a real
 * network fetch, and a few reloads can trip Remote Config's client-side
 * throttle. A throttled fetch looks exactly like the token not being in the
 * array, so rule throttling out before going and changing the console.
 */
export const MUM_HUB_FETCH_TTL_DEV_MS = 0;

/**
 * Parse the `mum_hub_allowlist` parameter value into tokens.
 *
 * FAIL CLOSED, and the failure is TOTAL rather than partial. Anything that is
 * not a JSON array of strings yields no tokens at all: not valid JSON, a JSON
 * object or number, or an array with so much as one non-string element. A
 * half-parsed array would mean a typo in the console silently granting the
 * wrong subset, which is worse than granting nobody — nobody is a symptom
 * somebody reports, a wrong subset is not.
 *
 * Never throws. `JSON.parse` on console-authored text is the one input here
 * that a human edits by hand, so it is also the one most likely to be
 * malformed.
 */
export function parseMumHubAllowlist(raw: unknown): string[] {
  if (typeof raw !== "string") return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  if (!parsed.every((entry) => typeof entry === "string")) return [];
  return parsed as string[];
}

/**
 * Does this customer get in? Global switch OR allow-list membership.
 *
 * OR, not AND, deliberately: `mum_hub_enabled` is the public rollout switch,
 * and the allow-list is how a handful of people see the feature before that
 * switch is ever flipped. Turning the global switch on must not require
 * emptying the list, and the list must keep working while the switch is off.
 *
 * A null token (signed in but with no email on the customer record, or a
 * browser with no WebCrypto) can never match, because the console holds
 * hashes and `null` is not one. Such a customer is left to the global switch
 * alone.
 */
export function decideMumHubAccess(input: {
  /** The activated `mum_hub_enabled` value. */
  globalEnabled: boolean;
  /** Tokens from `mum_hub_allowlist`, already parsed. */
  allowlist: readonly string[];
  /** This client's own locally computed token, or null. */
  token: string | null;
}): boolean {
  if (input.globalEnabled === true) return true;
  if (input.token === null) return false;
  return input.allowlist.includes(input.token);
}
